/**
 * Экспорт проекта в изображение.
 *
 * Пайплайн:
 *   1. Canvas размером с исходное изображение.
 *   2. Рисуем исходный растр.
 *   3. Применяем маски (blur/pixelize) честно к пикселям.
 *   4. Сериализуем SVG-слой аннотаций → data URL → <img> → рисуем поверх.
 *   5. canvas.convertToBlob → скачиваем файл.
 */
import type { BlurShape, ExportFormat, Project, Shape } from '../domain/types';
import { applyMask } from './raster';
import {
  arrowGeometry,
  catmullRomPath,
  numberArrowEnd,
  numberArrowStart,
  polylinePath,
  shapeBBox,
  markerPosition,
} from '../domain/geometry';

/** Скачать Blob под именем. */
function download(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Сгенерировать строку SVG-разметки всех видимых аннотаций в координатах изображения. */
function shapesToSvg(project: Project): string {
  const { image } = project;
  if (!image) return '';
  const w = image.width;
  const h = image.height;
  const parts: string[] = [];

  for (const s of [...project.shapes].sort((a, b) => a.z - b.z)) {
    if (!s.visible) continue;
    if (s.type === 'blur') continue; // маски применяем на растровом слое
    parts.push(renderShapeSvg(s));
  }

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">${parts.join('')}</svg>`;
}

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function renderShapeSvg(s: Shape): string {
  switch (s.type) {
    case 'arrow': {
      const g = arrowGeometry({ x: s.x, y: s.y }, { x: s.x2, y: s.y2 }, s.headStyle, s.headSize, {
        strokeWidth: s.strokeWidth,
        dash: s.dash,
        path: s.path,
      });
      let dashLayer = '';
      if (s.dash !== 'solid') {
        const dw = s.dash === 'dotted' ? Math.max(10, s.strokeWidth) : Math.max(4, s.strokeWidth * 0.5);
        const darr = g.dashArray ? `stroke-dasharray="${g.dashArray}"` : '';
        dashLayer = `<path d="${g.axisPath}" stroke="#ffffff" stroke-width="${dw}" stroke-linecap="round" ${darr} fill="none" opacity="${s.opacity}"/>`;
      }
      let m = '';
      if (s.marker) {
        const cx = (s.x + s.x2) / 2;
        const cy = (s.y + s.y2) / 2 - 28;
        const label = s.marker.kind === 'number' ? s.marker.value : '!';
        m = markerSvg(cx, cy, label, s.strokeColor);
      }
      return `<path d="${g.fillPath}" fill="${s.strokeColor}" stroke="none" opacity="${s.opacity}"/>${dashLayer}${m}`;
    }
    case 'rect': {
      const rs = s as Extract<Shape, { type: 'rect' }>;
      const isMask = rs.fillEnabled && (rs.fillMode === 'blur' || rs.fillMode === 'pixelize');
      
      let fillRect = '';
      if (!isMask && rs.fillEnabled) {
        fillRect = `<rect x="${rs.x}" y="${rs.y}" width="${rs.w}" height="${rs.h}" rx="${rs.cornerRadius}" ry="${rs.cornerRadius}" stroke="none" fill="${rs.fillColor}" opacity="${rs.opacity}"/>`;
      }
      
      let strokeRect = '';
      if (rs.strokeEnabled) {
        const sw = rs.strokeWidth;
        strokeRect = `<rect x="${rs.x - sw / 2}" y="${rs.y - sw / 2}" width="${rs.w + sw}" height="${rs.h + sw}" rx="${rs.cornerRadius + sw / 2}" ry="${rs.cornerRadius + sw / 2}" stroke="${rs.strokeColor}" stroke-width="${sw}" fill="none" opacity="${rs.opacity}"/>`;
      }
      
      const r = fillRect + strokeRect;
      let m = '';
      if (rs.marker) {
        const bb = shapeBBox(rs);
        const mp = markerPosition(bb);
        m = markerSvg(mp.x, mp.y - 24, rs.marker.kind === 'number' ? rs.marker.value : '!', rs.strokeColor);
      }
      return r + m;
    }

    case 'pencil': {
      const d = s.points.length > 2 ? catmullRomPath(s.points, s.closed) : polylinePath(s.points, s.closed);
      return `<path d="${d}" stroke="${s.strokeEnabled ? s.strokeColor : 'none'}" stroke-width="${s.strokeWidth}" stroke-linecap="round" stroke-linejoin="round" fill="${s.closed && s.fillEnabled ? s.fillColor : 'none'}" opacity="${s.opacity}"/>`;
    }
    case 'number': {
      let arrow = '';
      if (s.hasArrow) {
        const end = numberArrowEnd(s);
        const start = numberArrowStart(s);
        const g = arrowGeometry(start, end, s.headStyle, s.headSize, {
          strokeWidth: s.strokeWidth,
        });
        arrow = `<path d="${g.fillPath}" fill="${s.strokeColor}" stroke="none" opacity="${s.opacity}"/>`;
      }
      const fs = s.radius * 1.1;
      const rVal = s.strokeEnabled ? s.radius + s.strokeWidth / 2 : s.radius;
      const swVal = s.strokeEnabled ? s.strokeWidth : 0;
      const circle = `<circle cx="${s.x}" cy="${s.y}" r="${rVal}" fill="${s.fillEnabled ? s.fillColor : s.strokeColor}" stroke="${s.strokeEnabled ? s.strokeColor : 'none'}" stroke-width="${swVal}" opacity="${s.opacity}"/>`;
      const text = `<text x="${s.x}" y="${s.y + fs * 0.35}" text-anchor="middle" fill="${s.textColor}" font-size="${fs}" font-weight="bold" font-family="Inter, system-ui, sans-serif" opacity="${s.opacity}">${s.value}</text>`;
      return arrow + circle + text;
    }
    case 'text': {
      const lines = s.text.split('\n');
      const lh = s.fontSize * 1.25;
      const decor = [s.underline ? 'underline' : null, s.strikethrough ? 'line-through' : null].filter(Boolean).join(' ') || 'none';
      let bg = '';
      if (s.hasBackground) {
        const maxLen = Math.max(1, ...lines.map((l: string) => l.length));
        const tw = maxLen * s.fontSize * 0.6 + 16;
        const th = lines.length * lh + 12;
        bg = `<rect x="${s.x}" y="${s.y}" width="${tw}" height="${th}" rx="6" fill="${s.fillColor}" opacity="${s.opacity}"/>`;
      }
      const texts = lines.map((l: string, i: number) =>
        `<text x="${s.x + 8}" y="${s.y + s.fontSize + i * lh}" fill="${s.textColor}" font-size="${s.fontSize}" font-family="${s.fontFamily}" font-weight="${s.bold ? 'bold' : 'normal'}" font-style="${s.italic ? 'italic' : 'normal'}" text-decoration="${decor}" text-anchor="start" opacity="${s.opacity}">${esc(l)}</text>`
      ).join('');
      return bg + texts;
    }
    default:
      return '';
  }
}

function markerSvg(cx: number, cy: number, label: string | number, color: string): string {
  return `<circle cx="${cx}" cy="${cy}" r="16" fill="${color}"/><text x="${cx}" y="${cy + 5}" text-anchor="middle" fill="white" font-size="16" font-weight="bold" font-family="Inter, system-ui, sans-serif">${label}</text>`;
}

/**
 * Основной экспорт: рисует изображение, маски и аннотации в canvas, отдаёт Blob.
 */
export async function exportToBlob(
  project: Project,
  format: ExportFormat = 'png',
  options?: { scale?: number; quality?: number },
): Promise<Blob> {
  if (!project.image) throw new Error('Нет изображения для экспорта');
  const scale = options?.scale ?? 1;
  const quality = options?.quality ?? 0.92;

  const w = project.image.width;
  const h = project.image.height;
  
  const bg = project.background;
  const pad = bg.enabled ? bg.padding : 0;
  
  const exportW = w + pad * 2;
  const exportH = h + pad * 2;

  const canvas = document.createElement('canvas');
  canvas.width = Math.round(exportW * scale);
  canvas.height = Math.round(exportH * scale);
  const ctx = canvas.getContext('2d')!;
  ctx.scale(scale, scale);

  // 1) Отрисовка фона (простой плоский прямоугольник без скруглений и теней)
  if (bg.enabled) {
    ctx.save();
    ctx.fillStyle = bg.color;
    ctx.fillRect(0, 0, exportW, exportH);
    ctx.restore();
  }

  // Смещаем рисование содержимого на размер отступа (padding)
  ctx.save();
  ctx.translate(pad, pad);

  if (bg.enabled) {
    ctx.save();
    if (bg.shadowEnabled) {
      ctx.fillStyle = '#ffffff';
      
      // Слой тени 1: глубокая мягкая тень (20px вниз, 40px размытие)
      ctx.save();
      ctx.shadowColor = 'rgba(0, 0, 0, 0.12)';
      ctx.shadowBlur = 40;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 20;
      ctx.beginPath();
      ctx.roundRect(0, 0, w, h, bg.cornerRadius);
      ctx.fill();
      ctx.restore();
      
      // Слой тени 2: средняя очерчивающая тень (8px вниз, 16px размытие)
      ctx.save();
      ctx.shadowColor = 'rgba(0, 0, 0, 0.06)';
      ctx.shadowBlur = 16;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 8;
      ctx.beginPath();
      ctx.roundRect(0, 0, w, h, bg.cornerRadius);
      ctx.fill();
      ctx.restore();
    }
    ctx.beginPath();
    ctx.roundRect(0, 0, w, h, bg.cornerRadius);
    ctx.clip();
  }

  // 2) Исходный растр
  const img = await loadImageEl(project.image.src);
  ctx.drawImage(img, 0, 0, w, h);

  // 3) Маски — в порядке z
  const masks: { x: number; y: number; w: number; h: number; mode: 'blur' | 'pixelize'; intensity: number; z: number }[] = [];
  for (const s of project.shapes) {
    if (!s.visible) continue;
    if (s.type === 'blur' && s.w > 0 && s.h > 0) {
      masks.push({
        x: s.x,
        y: s.y,
        w: s.w,
        h: s.h,
        mode: s.mode,
        intensity: s.intensity,
        z: s.z,
      });
    } else if (s.type === 'rect' && s.fillEnabled && (s.fillMode === 'blur' || s.fillMode === 'pixelize') && s.w > 0 && s.h > 0) {
      const rs = s as Extract<Shape, { type: 'rect' }>;
      masks.push({
        x: rs.x,
        y: rs.y,
        w: rs.w,
        h: rs.h,
        mode: rs.fillMode as 'blur' | 'pixelize',
        intensity: rs.intensity ?? 12,
        z: rs.z,
      });
    }
  }
  
  masks.sort((a, b) => a.z - b.z);
  for (const m of masks) {
    applyMask(ctx, m.x, m.y, m.w, m.h, m.mode, m.intensity);
  }

  // 4) SVG-аннотации → img → canvas
  const svgStr = shapesToSvg(project);
  if (svgStr) {
    const svgBlob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' });
    const svgUrl = URL.createObjectURL(svgBlob);
    try {
      const svgImg = await loadImageEl(svgUrl);
      ctx.drawImage(svgImg, 0, 0, w, h);
    } finally {
      URL.revokeObjectURL(svgUrl);
    }
  }

  if (bg.enabled) {
    ctx.restore();
  }

  ctx.restore();

  // 5) Blob → вернуть
  const mime = format === 'png' ? 'image/png' : format === 'jpeg' ? 'image/jpeg' : 'image/webp';
  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, mime, quality),
  );
  if (!blob) throw new Error('Не удалось создать файл');
  return blob;
}

export async function exportImage(
  project: Project,
  format: ExportFormat = 'png',
  options?: { scale?: number; quality?: number },
): Promise<void> {
  const blob = await exportToBlob(project, format, options);
  const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
  download(blob, `${project.name || 'annotator'}-${stamp}.${format}`);
}

function loadImageEl(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('image load failed'));
    img.src = src;
  });
}
